<script>
import lazyLoadComponent from '@router/lazyload-component'

import {
  genderFilter,
  orderStatusFilter,
  orderSourceFilter,
  prepaidOrderStatusFilter,
} from '@utils/filter-value-to-text'
import formatDate from '@utils/format-date'
import { PortalTarget } from 'portal-vue'

export default {
  name: 'PrepaidOrderDetail',
  components: {
    PortalTarget,
    BaseButton: () => lazyLoadComponent(import('@components/BaseButton')),
    BaseStateLabel: () =>
      lazyLoadComponent(import('@components/BaseStateLabel')),
    BaseCollapse: () => lazyLoadComponent(import('@components/BaseCollapse')),
    OrderForm: () => lazyLoadComponent(import('@components/PrepaidOrderForm')),
    OrderChangeStatusDialog: () =>
      lazyLoadComponent(import('@components/OrderChangeStatusDialog')),
    OrderMemberDialog: () =>
      lazyLoadComponent(import('@components/OrderMemberDialog')),
    OrderDetail: () => lazyLoadComponent(import('@components/OrderDetail')),
  },
  filters: {
    orderStatusFilter,
    prepaidOrderStatusFilter,
    genderFilter,
    orderSourceFilter,
    formatDate,
  },
  props: {
    defaultValue: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      mode: 'view', // view 檢視、edit 編輯
      shareDialogVisible: false,
      cancelDialogVisible: false,
      memberDialogVisible: false,
      tryingSendEmail: false,
      tryingSendSMS: false,
      shareLink: '',
      showCancelDeductOrder: false,
      drawerVisible: false,
      drawerOrderData: '',
    }
  },
  computed: {
    isPaid() {
      return this.defaultValue.status === 1
    },
    filterDeductOrders() {
      return this.showCancelDeductOrder
        ? this.defaultValue.deductOrders
        : this.defaultValue.deductOrders.filter((order) => order.status === 1)
    },
  },
  methods: {
    updateMode(value) {
      this.mode = value
    },
    async tryToGetOrder(orderId) {
      this.drawerOrderData = await this.$store.dispatch(
        'order/fetchSingle',
        orderId
      )
      this.drawerVisible = true
    },
    async handleStore() {
      const updatedValue = await this.$store.dispatch(
        'prepaid/fetchOrderSingle',
        this.defaultValue.id
      )
      this.$emit('update:defaultValue', updatedValue)
      this.$emit('update')
      this.updateMode('view')
    },
    async tryToSendEmailOrSMS(router) {
      router === 'email'
        ? (this.tryingSendEmail = true)
        : (this.tryingSendSMS = true)
      try {
        await this.$store.dispatch('prepaid/sendLink', {
          orderId: this.defaultValue.id,
          router,
        })
        this.$alert(`已發送付款連結至 ${router}`, `${router} 發送成功！`, {
          confirmButtonText: '確認',
          center: true,
          callback: () => {
            router === 'email'
              ? (this.tryingSendEmail = false)
              : (this.tryingSendSMS = false)
          },
        })
      } catch (error) {
        router === 'email'
          ? (this.tryingSendEmail = false)
          : (this.tryingSendSMS = false)
      }
    },
    showCancelDialog() {
      this.cancelDialogVisible = true
    },
    showMemberDialog() {
      this.memberDialogVisible = true
    },
  },
}
</script>

<template>
  <div>
    <h3>{{ `#${defaultValue.orderNo}` }}</h3>
    <p :class="$style.meta">
      <span>{{ defaultValue.created | formatDate('dateTime') }} 成立訂單</span>
      <BaseStateLabel state="info" text
        >訂單來源：{{ defaultValue.source | orderSourceFilter }}</BaseStateLabel
      >
    </p>
    <BaseStateLabel :state="defaultValue.status | orderStatusFilter('color')">{{
      defaultValue.status | orderStatusFilter('text')
    }}</BaseStateLabel>

    <div :class="$style.header">
      <h5 :class="$style.title">{{ defaultValue.productName }}</h5>
      <div>{{ defaultValue.date | formatDate }} {{ defaultValue.time }}</div>
    </div>

    <!-- 編輯訂單 -->
    <OrderForm
      v-if="mode === 'edit'"
      :default-value="defaultValue"
      @store="handleStore"
      @cancel="updateMode('view')"
    />

    <template v-if="mode === 'view'">
      <BaseCollapse title="訂單變更紀錄">
        <el-timeline>
          <el-timeline-item
            v-for="log in defaultValue.history"
            :key="log.id"
            :timestamp="log.created | formatDate('dateTime')"
            type="primary"
            placement="top"
          >
            {{ log.description }}
            <div v-if="log.note">{{ log.note }}</div>
          </el-timeline-item>
        </el-timeline>
      </BaseCollapse>
      <div :class="$style.params">
        <div :class="$style['params__item']">
          <div>訂購項目</div>
          <div>售價</div>
          <div>數量</div>
        </div>
        <div :class="$style['params__item']">
          <div>{{ defaultValue.prepaidTitle }}</div>
          <div>{{ defaultValue.priceTotal }}</div>
          <div>x1</div>
        </div>

        <template>
          <div :class="$style.total">
            <div :class="$style['total__item']">
              <div>付款金額</div>
              <div>{{ defaultValue.priceTotal }}</div>
            </div>
          </div>
        </template>
      </div>
      <BaseCollapse title="客戶使用歷程">
        <el-switch
          v-model="showCancelDeductOrder"
          active-text="顯示待確認與已取消訂單"
        ></el-switch>
        <el-timeline>
          <el-timeline-item
            v-for="order in filterDeductOrders"
            :key="order.id"
            :timestamp="order.created | formatDate('dateTime')"
            :type="order.status | prepaidOrderStatusFilter('color')"
          >
            <BaseStateLabel
              :state="order.status | prepaidOrderStatusFilter('color')"
              text
              >{{
                order.status | prepaidOrderStatusFilter('text')
              }}</BaseStateLabel
            >

            <el-link
              :class="$style['history-title']"
              @click="tryToGetOrder(order.id)"
              >{{ order.productTitle }}</el-link
            >
            <span :class="$style['history-meta']"
              >・{{ order.startDate | formatDate('dateTime') }}</span
            >
          </el-timeline-item>
        </el-timeline>
      </BaseCollapse>

      <h5 :class="$style.title">客戶資訊</h5>
      <ul :class="$style.info">
        <li
          >{{ defaultValue.fullName }}
          {{ defaultValue.gender | genderFilter }}</li
        >
        <li>{{ `${defaultValue.phoneCode} ${defaultValue.phoneNumber}` }}</li>
        <li>{{ defaultValue.email }}</li>
      </ul>
      <div :class="$style.buttons">
        <BaseButton icon="clipboard-list" @click="showMemberDialog"
          >查看客戶資訊</BaseButton
        >
        <template v-if="defaultValue.status === 2">
          <BaseButton icon="paper-plane" @click="tryToSendEmailOrSMS('email')"
            >發送付款連結至 Email</BaseButton
          >
          <BaseButton icon="sms" @click="tryToSendEmailOrSMS('sms')"
            >發送付款連結至簡訊</BaseButton
          >
        </template>
        <template v-if="defaultValue.status !== 0">
          <BaseButton icon="edit" @click="updateMode('edit')"
            >編輯訂單</BaseButton
          >
          <BaseButton icon="ban" @click="showCancelDialog">取消訂單</BaseButton>
        </template>
      </div>
      <OrderChangeStatusDialog
        :visible.sync="cancelDialogVisible"
        :order="{ ...defaultValue, status: 0 }"
        @cancelled="handleStore"
      />
      <OrderMemberDialog
        :visible.sync="memberDialogVisible"
        :member-id="defaultValue.memberId"
      />
    </template>
    <el-drawer :visible.sync="drawerVisible" append-to-body size="50%">
      <!-- header -->
      <PortalTarget name="drawer-header" slot="title"></PortalTarget>
      <OrderDetail ref="orderDetail" :default-value.sync="drawerOrderData" />
      <el-button
        class="is-fullwidth is-drawer--bottom"
        plain
        @click="drawerVisible = false"
        >回到客戶使用歷程</el-button
      >
    </el-drawer>
  </div>
</template>

<style lang="scss" module>
@import '@design';
:global {
  .el-timeline {
    margin-top: 15px;
  }
}

.meta {
  display: flex;
  justify-content: space-between;
}
.header {
  margin-bottom: 15px;
}
.title:not(:first-child) {
  margin-top: 30px;
}
.info {
  padding-inline-start: 0;
  > li {
    padding: 5px 0;
  }
}
.params {
  padding: 5px 15px;
  margin-top: 10px;
  margin-bottom: 10px;
  font-size: 14px;
  line-height: 40px;
  background: white;

  &__item {
    display: flex;
    justify-content: space-between;

    &:first-child {
      color: $color-text-placeholder;
    }
    > * {
      flex: 1;
      &:first-child {
        flex: 2;
      }
      &:not(:first-child) {
        text-align: right;
      }
    }
  }
}
.total {
  border-top: 1px solid $color-border-base;
  &__item {
    display: flex;
    justify-content: space-between;

    &--discount {
      color: $color-state-danger;
    }
    &:not(.total__item--discount) > *:first-child {
      color: $color-text-placeholder;
    }
  }
}
.history {
  &-title {
    margin-left: 10px;
  }
  &-meta {
    color: $color-text-placeholder;
  }
}

.buttons {
  margin-top: 30px;
  border-top: 1px solid $color-border-base;
  border-bottom: 1px solid $color-border-base;
}
</style>
